import React, { useState } from 'react';
import { FiSun, FiMoon, FiBell, FiMenu, FiLogOut, FiUser, FiSettings } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useTheme } from '../../../context/ThemeContext';
import styles from './Header.module.css';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className={styles.header}>
      <div className={styles.headerLeft}>
        <button className={styles.menuButton} onClick={onMenuClick} aria-label="Toggle menu">
          <FiMenu />
        </button>

        <div className={styles.searchBar}>
          <input
            type="text"
            placeholder="Search articles, users, categories..."
            className={styles.searchInput}
          />
        </div>
      </div>

      <div className={styles.headerRight}>
        <button
          className={styles.iconButton}
          onClick={toggleTheme}
          aria-label="Toggle theme"
          title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
        >
          {theme === 'light' ? <FiMoon /> : <FiSun />}
        </button>

        {/* Notifications */}
        <button className={styles.iconButton} aria-label="Notifications" title="Notifications">
          <FiBell />
          <span className={styles.badge}>3</span>
        </button>

        {/* Profile Menu */}
        <div className={styles.profileContainer}>
          <button
            className={styles.profileButton}
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            aria-label="Profile menu"
          >
            <img
              src={user?.profilePhoto.url || `https://ui-avatars.com/api/?name=${user?.name}`}
              alt={user?.name}
              className={styles.profileAvatar}
            />
            <div className={styles.profileInfo}>
              <span className={styles.profileName}>{user?.name}</span>
              <span className={styles.profileRole}>{user?.role}</span>
            </div>
          </button>

          {showProfileMenu && (
            <div className={styles.profileMenu}>
              <div className={styles.profileMenuHeader}>
                <img
                  src={user?.profilePhoto.url || `https://ui-avatars.com/api/?name=${user?.name}`}
                  alt={user?.name}
                  className={styles.profileMenuAvatar}
                />
                <div>
                  <p className={styles.profileMenuName}>{user?.name}</p>
                  <p className={styles.profileMenuEmail}>{user?.email}</p>
                </div>
              </div>

              <div className={styles.profileMenuDivider}></div>

              <button
                className={styles.profileMenuItem}
                onClick={() => {
                  navigate('/profile');
                  setShowProfileMenu(false);
                }}
              >
                <FiUser />
                <span>My Profile</span>
              </button>

              <button
                className={styles.profileMenuItem}
                onClick={() => {
                  navigate('/settings');
                  setShowProfileMenu(false);
                }}
              >
                <FiSettings />
                <span>Settings</span>
              </button>

              <div className={styles.profileMenuDivider}></div>

              <button className={`${styles.profileMenuItem} ${styles.logoutButton}`} onClick={handleLogout}>
                <FiLogOut />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Overlay for profile menu */}
      {showProfileMenu && (
        <div className={styles.overlay} onClick={() => setShowProfileMenu(false)}></div>
      )}
    </header>
  );
};

export default Header;